import {
  Component,
  Suspense,
  lazy,
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { Seo } from "./Seo";
import { Terminal } from "./terminal/Terminal";

/**
 * laureldaigneault.com — an interactive terminal résumé with a secret.
 *
 * The site opens as a full-screen fake shell (src/terminal); all content
 * comes from src/content.ts and <Seo> adds a hidden, crawlable mirror.
 * Typing `exit` doesn't close the tab — the terminal shrinks away (into
 * the "Laurel CV" desktop icon, which reopens it) to reveal what it was
 * running on all along: SliceOS (src/os), a fictional desktop OS. No boot
 * sequence — you land straight on the desktop.
 *
 * Once you've exited, the machine is yours: the mode persists in
 * localStorage, so every reload — including the OS's own shutdown →
 * "NO SIGNAL" → click cycle — boots SliceOS with its full boot sequence
 * (that boot is where hold-Q opens the operator setup). Only a memory
 * wipe from that operator menu unlinks the operator and returns the site
 * to the portfolio terminal. Entering via `exit` itself skips the boot
 * and lands straight on the desktop.
 */

const loadOs = () => import("./os/OsRoot");
const OsRoot = lazy(loadOs);

/** Persisted once the visitor exits the shell; cleared by a memory wipe
 * in the boot-time operator setup (see src/os/index.tsx). */
const MODE_KEY = "laurelos_mode";

/** How long the terminal takes to fly into the desktop icon. */
const SHRINK_MS = 800;

type Mode = "terminal" | "os";

interface ShrinkTarget {
  x: number;
  y: number;
  scaleX: number;
  scaleY: number;
}

/** Has this visitor already exited into the OS? */
function osIsHome(): boolean {
  try {
    return localStorage.getItem(MODE_KEY) === "os";
  } catch {
    // private mode / no storage — just treat it as a fresh visit
    return false;
  }
}

/** Locate the "Laurel CV" desktop icon so the terminal can fly into it. */
function findCvIconRect(): DOMRect | null {
  for (const el of document.querySelectorAll(".itemicon-name")) {
    if (el.textContent?.trim() === "Laurel CV") {
      return (el.closest("[data-drag-id]") ?? el).getBoundingClientRect();
    }
  }
  return null;
}

/** Shown while the (code-split) OS bundle loads: a dark pre-boot screen.
 * With the idle-time prefetch below this should almost never be visible. */
function PowerOn() {
  return <div style={{ position: "fixed", inset: 0, background: "#111" }} />;
}

/**
 * If the OS chunk fails to load (flaky network, stale deploy), don't leave
 * a dead black tab — fall back to the résumé.
 */
class OsBoundary extends Component<
  { onFail: () => void; children: ReactNode },
  { failed: boolean }
> {
  state = { failed: false };
  static getDerivedStateFromError() {
    return { failed: true };
  }
  componentDidCatch(error: unknown) {
    console.error("SliceOS failed to load — returning to the terminal.", error);
    this.props.onFail();
  }
  render() {
    return this.state.failed ? null : this.props.children;
  }
}

export function App() {
  // Once exited, reloads boot SliceOS (with its boot sequence); a fresh
  // visitor — or one who wiped memory in the operator setup — starts at
  // the terminal.
  const [osHome] = useState(osIsHome);
  const [mode, setMode] = useState<Mode>(osHome ? "os" : "terminal");
  // The boot sequence plays on every (re)boot into the OS; only entering
  // via `exit` drops straight onto the desktop.
  const [skipBoot, setSkipBoot] = useState(false);
  // The terminal stays mounted (on top of the OS) while it shrinks away.
  const [terminalMounted, setTerminalMounted] = useState(!osHome);
  const [shrinking, setShrinking] = useState(false);
  const [shrinkTarget, setShrinkTarget] = useState<ShrinkTarget | null>(null);
  // Remounts the boundary (and retries the chunk) on each entry attempt.
  const [osAttempt, setOsAttempt] = useState(0);
  const exiting = useRef(false);

  // Warm the OS chunk while the visitor reads the terminal, so `exit`
  // reveals the desktop with no loading beat.
  useEffect(() => {
    if (mode !== "terminal") return;
    const warm = () => void loadOs().catch(() => {});
    if (typeof window.requestIdleCallback === "function") {
      const id = window.requestIdleCallback(warm, { timeout: 4000 });
      return () => window.cancelIdleCallback(id);
    }
    const t = window.setTimeout(warm, 1500);
    return () => window.clearTimeout(t);
  }, [mode]);

  const exitToOs = useCallback(() => {
    if (exiting.current) return;
    exiting.current = true;
    // From here on, this machine boots SliceOS — until a memory wipe.
    try {
      localStorage.setItem(MODE_KEY, "os");
    } catch {
      // no storage — the OS still opens, it just won't survive a reload
    }
    // The terminal stamps its phosphor theme on <html>; don't leave it
    // dangling under the OS.
    delete document.documentElement.dataset.theme;
    // Mount the desktop behind the terminal (no boot sequence)…
    setSkipBoot(true);
    setOsAttempt((n) => n + 1);
    setMode("os");

    // …wait for the desktop to paint its icons, then fly the terminal into
    // the "Laurel CV" icon. If the icon never shows (moved, renamed, slow
    // chunk), shrink to center as a fallback. The wrapper unmounts on
    // transitionend; this timeout is only a safety net for browsers that
    // skip the transition entirely.
    const begin = (target: ShrinkTarget | null) => {
      setShrinkTarget(target);
      setShrinking(true);
      window.setTimeout(() => setTerminalMounted(false), SHRINK_MS + 2000);
    };
    const deadline = performance.now() + 2000;
    const seek = () => {
      const rect = findCvIconRect();
      if (rect) {
        begin({
          x: rect.left + rect.width / 2 - window.innerWidth / 2,
          y: rect.top + rect.height / 2 - window.innerHeight / 2,
          scaleX: rect.width / window.innerWidth,
          scaleY: rect.height / window.innerHeight,
        });
      } else if (performance.now() < deadline) {
        requestAnimationFrame(seek);
      } else {
        begin(null);
      }
    };
    requestAnimationFrame(seek);
  }, []);

  const onOsFailed = useCallback(() => {
    // Most likely a stale deploy (old module graph, renamed chunks). One
    // guarded reload fetches the fresh deploy; a second failure falls back
    // to the terminal instead of looping.
    try {
      if (!sessionStorage.getItem("sliceos_chunk_retry")) {
        sessionStorage.setItem("sliceos_chunk_retry", "1");
        window.location.reload();
        return;
      }
    } catch {
      // no storage — skip the retry, fall back directly
    }
    exiting.current = false;
    setMode("terminal");
    setShrinking(false);
    setShrinkTarget(null);
    setTerminalMounted(true);
  }, []);

  const shrinkTransform = !shrinking
    ? "none"
    : shrinkTarget
      ? `translate(${shrinkTarget.x}px, ${shrinkTarget.y}px) scale(${shrinkTarget.scaleX}, ${shrinkTarget.scaleY})`
      : "scale(0.12)";

  return (
    <>
      {mode === "os" && (
        <OsBoundary key={osAttempt} onFail={onOsFailed}>
          <Suspense fallback={<PowerOn />}>
            <OsRoot skipBoot={skipBoot} />
          </Suspense>
        </OsBoundary>
      )}

      {terminalMounted && (
        <div
          onTransitionEnd={(e) => {
            if (shrinking && e.propertyName === "transform") {
              setTerminalMounted(false);
            }
          }}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 50,
            transformOrigin: "50% 50%",
            transition: `transform ${SHRINK_MS}ms cubic-bezier(0.22, 1, 0.36, 1), opacity 300ms ease ${SHRINK_MS - 320}ms, border-radius ${SHRINK_MS}ms ease`,
            transform: shrinkTransform,
            opacity: shrinking ? 0 : 1,
            borderRadius: shrinking ? 12 : 0,
            overflow: "hidden",
            pointerEvents: shrinking ? "none" : "auto",
          }}
        >
          <Seo />
          <Terminal onExit={exitToOs} />
        </div>
      )}
    </>
  );
}
